import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import BoardGamesManager from './BoardGamesManager';
import BookingManager from './BookingManager';
import EventsManager from './EventsManager';
import EditClubProfile from './EditClubProfile';
import CreateClub from './CreateClub';
import { getImageUrl } from '../utils/imageHelper';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [clubs, setClubs] = useState([]);
  const [selectedClub, setSelectedClub] = useState(null);
  const [activeFeature, setActiveFeature] = useState('board-games'); // 'board-games' | 'bookings' | 'events'
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [profileResult, clubsResult] = await Promise.all([
        authService.getProfile(),
        authService.authenticatedFetch(`${API_URL}/owned-clubs`)
      ]);

      if (profileResult.success) {
        setUser(profileResult.user);
      }

      if (clubsResult.success) {
        setClubs(clubsResult.data);
        if (clubsResult.data.length > 0) {
          setSelectedClub(clubsResult.data[0]);
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await authService.logout();
    navigate('/login');
  };

  const handleClubUpdate = (updatedClub) => {
    setClubs(clubs.map(club => club.id === updatedClub.id ? updatedClub : club));
    setSelectedClub(updatedClub);
  };

  const handleClubCreated = (newClub) => {
    setClubs([...clubs, newClub]);
    setSelectedClub(newClub);
    setShowCreateModal(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <h1 className="text-xl font-bold text-gray-900">BoardVoyage Admin</h1>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-700">
                {user?.first_name} {user?.last_name}
              </span>
              <button
                onClick={handleLogout}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Feature Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                <button
                  onClick={() => setActiveFeature('board-games')}
                  className={`${
                    activeFeature === 'board-games'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  <svg className="inline-block w-5 h-5 mr-2 -mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  Board Games Management
                </button>
                <button
                  onClick={() => setActiveFeature('bookings')}
                  className={`${
                    activeFeature === 'bookings'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  <svg className="inline-block w-5 h-5 mr-2 -mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Booking Management
                </button>
                <button
                  onClick={() => setActiveFeature('events')}
                  className={`${
                    activeFeature === 'events'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  <svg className="inline-block w-5 h-5 mr-2 -mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                  </svg>
                  Events Management
                </button>
              </nav>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-indigo-600 rounded-md hover:bg-indigo-700 transition-colors"
                >
                  <svg className="inline-block w-4 h-4 mr-2 -mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Create Club
                </button>
                {selectedClub && (
                  <button
                    onClick={() => setShowEditModal(true)}
                    className="px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100 transition-colors"
                  >
                    <svg className="inline-block w-4 h-4 mr-2 -mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Edit Club Profile
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content - List/Details Layout */}
        <div className="grid grid-cols-12 gap-6">
          {/* Clubs List Sidebar */}
          <div className="col-span-3 bg-white rounded-lg shadow overflow-hidden">
            <div className="px-4 py-5 border-b border-gray-200 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                Select Club
              </h3>
            </div>
            <div className="divide-y divide-gray-200 max-h-[calc(100vh-250px)] overflow-y-auto">
              {clubs.map((club) => (
                <button
                  key={club.id}
                  onClick={() => setSelectedClub(club)}
                  className={`w-full text-left px-4 py-4 hover:bg-gray-50 transition-colors ${
                    selectedClub?.id === club.id ? 'bg-indigo-50 border-l-4 border-indigo-500' : ''
                  }`}
                >
                  <div className="flex items-center">
                    {club.image ? (
                      <img
                        src={getImageUrl(club.image)}
                        alt={club.name}
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg">
                        {club.name.charAt(0)}
                      </div>
                    )}
                    <div className="ml-3 flex-1">
                      <p className={`text-sm font-medium ${
                        selectedClub?.id === club.id ? 'text-indigo-900' : 'text-gray-900'
                      }`}>
                        {club.name}
                      </p>
                      <p className="text-xs text-gray-500 truncate">{club.location || 'No location'}</p>
                    </div>
                  </div>
                </button>
              ))}
              {clubs.length === 0 && (
                <div className="px-4 py-8 text-center text-gray-500 text-sm">
                  No clubs available
                </div>
              )}
            </div>
          </div>

          {/* Details Panel */}
          <div className="col-span-9">
            {selectedClub ? (
              <>
                {activeFeature === 'board-games' && (
                  <BoardGamesManager club={selectedClub} />
                )}
                {activeFeature === 'bookings' && (
                  <BookingManager club={selectedClub} user={user} />
                )}
                {activeFeature === 'events' && (
                  <EventsManager club={selectedClub} />
                )}
              </>
            ) : (
              <div className="bg-white rounded-lg shadow p-12 text-center">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No club selected</h3>
                <p className="mt-1 text-sm text-gray-500">Select a club from the list to manage its features.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Club Profile Modal */}
      {showEditModal && selectedClub && (
        <EditClubProfile
          club={selectedClub}
          onClose={() => setShowEditModal(false)}
          onUpdate={handleClubUpdate}
        />
      )}

      {/* Create Club Modal */}
      {showCreateModal && (
        <CreateClub
          isModal={true}
          onClose={() => setShowCreateModal(false)}
          onClubCreated={handleClubCreated}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
